import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));
  const relatedArticles = article ? getRelatedArticles(article.id, 3) : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <div className="pt-16">
          <div className="px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该爆料可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm hover:bg-primary-600 transition-colors duration-200"
            >
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const categoryName = categories.find(c => c.slug === article.category)?.name || article.category;
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '爆料专区',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://baoliao.example.com/article/${article.id}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <div className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-2.5 py-1 text-xs bg-primary-100 text-primary-700 rounded-full whitespace-nowrap"
                >
                  {categoryName}
                </Link>
                {article.isExclusive && (
                  <span className="px-2.5 py-1 text-xs bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                    独家爆料
                  </span>
                )}
                {article.isVideo && (
                  <span className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap flex items-center gap-1">
                    <i className="ri-video-line"></i> 含视频
                  </span>
                )}
              </div>

              <h1 className="text-xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(1)}万 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i> {article.comments} 评论
                </span>
              </div>

              <div className="rounded-lg overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex items-center gap-2 mt-8 pt-6 border-t border-background-200/70 flex-wrap">
                <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
                {article.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-lg font-bold text-foreground-950 mb-5">相关爆料</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map(rel => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1">{rel.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}